import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';

import { backendRequestHeaders, getBackendBaseUrl, resolveBearerToken } from '@/api/backendAuth';
import { useAppSelector } from '@/app/hooks';
import { useTracking } from '@/hooks/useTracking';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';

import queryKeys from '../../modules/shared/query/queryKeys';

import { extractWorkspaceId } from './useCalculateChurn';

export interface GenerateChurnSignalsResponse {
  message: string;
  result: {
    signalsCreated: number;
    signalsUpdated: number;
    totalProcessed: number;
    signals: unknown[];
  };
  debug?: { tenantId: string; workspaceId: string; audienceCount: number };
}

async function postGenerateChurnSignals(workspaceId: string, token: string | undefined) {
  const base = getBackendBaseUrl();
  const headers = backendRequestHeaders(token);
  if (!headers.Authorization) {
    throw new Error('Missing Bearer token for generate churn signals.');
  }

  const { data } = await axios.post<GenerateChurnSignalsResponse>(
    `${base}/workspaces/${workspaceId}/churn-signals/generate`,
    {},
    { headers },
  );

  return data;
}

export function useGenerateChurnSignals() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { trackEvent } = useTracking();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const workspaceId = extractWorkspaceId(workspace);
  const sessionJwt = user?.signInUserSession?.idToken?.jwtToken;
  const bearer = resolveBearerToken(sessionJwt);

  return useMutation({
    mutationFn: () => postGenerateChurnSignals(workspaceId, bearer || undefined),
    onSuccess: async (res) => {
      trackEvent('churn_signals_generated', {
        signals_created: res.result?.signalsCreated,
        signals_updated: res.result?.signalsUpdated,
        members_processed: res.result?.totalProcessed,
      });
      await queryClient.invalidateQueries([queryKeys.accountMetrics, workspaceId]);
      await queryClient.refetchQueries([queryKeys.accountMetrics, workspaceId]);
    },
  });
}
